import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { reportsAPI } from '../services/api';
import './TpcAccessVsCornerstoneReport.css';

function formatDate(str) {
  if (!str) return '—';
  const s = String(str).trim();
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? str : `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function parseDateToNumber(value) {
  if (!value) return null;
  const s = String(value).trim();
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const n = Date.parse(`${ymd[1]}-${ymd[2]}-${ymd[3]}T00:00:00`);
    return Number.isNaN(n) ? null : n;
  }
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (mdy) {
    const year = mdy[3].length === 2 ? `20${mdy[3]}` : mdy[3];
    const n = Date.parse(`${year}-${String(mdy[1]).padStart(2, '0')}-${String(mdy[2]).padStart(2, '0')}T00:00:00`);
    return Number.isNaN(n) ? null : n;
  }
  const d = Date.parse(s);
  return Number.isNaN(d) ? null : d;
}

function formatDateForCsv(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[2]}/${ymd[3]}/${ymd[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

function csvEscape(value) {
  const str = value == null ? '' : String(value);
  if (/[,"\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

function downloadCsv(filename, headerLabels, rows) {
  const csvLines = [headerLabels.map(csvEscape).join(',')];
  rows.forEach((row) => {
    csvLines.push(row.map(csvEscape).join(','));
  });
  const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

function ReconciliationGrid({ sectionKey, title, rows, columns, emptyText, onPrintSection, onExportSectionCsv }) {
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState([]);
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [draftFilters, setDraftFilters] = useState({});
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 260 });
  const filterButtonRefs = useRef({});

  const visibleColumns = useMemo(() => columns.filter((c) => c.visibleByDefault !== false), [columns]);

  const facets = useMemo(() => {
    const map = {};
    visibleColumns.forEach((col) => {
      if (col.filter?.kind === 'multiSelect') {
        map[col.key] = Array.from(
          new Set((rows || []).map((r) => (r[col.key] == null ? '' : String(r[col.key]).trim())).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
      }
    });
    return map;
  }, [rows, visibleColumns]);

  const filteredRows = useMemo(() => {
    return (rows || []).filter((row) => {
      for (const col of visibleColumns) {
        const filter = filters[col.key];
        if (!filter) continue;
        const raw = row[col.key];
        if (col.filter?.kind === 'textContains') {
          const text = String(filter.text || '').trim().toLowerCase();
          if (!text) continue;
          if (!String(raw || '').toLowerCase().includes(text)) return false;
        } else if (col.filter?.kind === 'multiSelect') {
          const values = Array.isArray(filter.values) ? filter.values : [];
          if (values.length === 0) continue;
          const value = String(raw || '').trim();
          if (!values.includes(value)) return false;
        } else if (col.filter?.kind === 'dateRange') {
          const n = parseDateToNumber(raw);
          if (filter.from) {
            const fromN = parseDateToNumber(filter.from);
            if (fromN != null && (n == null || n < fromN)) return false;
          }
          if (filter.to) {
            const toN = parseDateToNumber(filter.to);
            if (toN != null && (n == null || n > toN)) return false;
          }
        }
      }
      return true;
    });
  }, [rows, filters, visibleColumns]);

  const displayedRows = useMemo(() => {
    if (!Array.isArray(sort) || !sort[0]?.key) return filteredRows;
    const key = sort[0].key;
    const dir = sort[0].dir === 'desc' ? -1 : 1;
    const column = visibleColumns.find((c) => c.key === key);
    const isDate = column?.type === 'date';
    const isCsVisitDisplay = key === 'cs_latest_visit_display';
    return [...filteredRows].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (isDate) {
        const an = parseDateToNumber(av);
        const bn = parseDateToNumber(bv);
        if (an == null && bn == null) return 0;
        if (an == null) return -1 * dir;
        if (bn == null) return 1 * dir;
        return (an - bn) * dir;
      }
      if (isCsVisitDisplay) {
        const rank = (v) => {
          if (v == null || v === '') return 0;
          if (v === 'OOR') return 1;
          return 2;
        };
        const ra = rank(av);
        const rb = rank(bv);
        if (ra !== rb) return (ra - rb) * dir;
        if (ra === 2) {
          const an = parseDateToNumber(av);
          const bn = parseDateToNumber(bv);
          if (an == null && bn == null) return 0;
          if (an == null) return -1 * dir;
          if (bn == null) return 1 * dir;
          return (an - bn) * dir;
        }
        return 0;
      }
      return String(av || '').localeCompare(String(bv || '')) * dir;
    });
  }, [filteredRows, sort, visibleColumns]);

  useEffect(() => {
    if (!openFilterKey) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (target && target.closest && target.closest('.tpc-can-header-filter-wrap')) return;
      if (target && target.closest && target.closest('.tpc-can-filter-popover-portal')) return;
      setOpenFilterKey(null);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpenFilterKey(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openFilterKey]);

  useEffect(() => {
    if (!openFilterKey) return undefined;
    const updatePosition = () => {
      const button = filterButtonRefs.current[openFilterKey];
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const openColumn = visibleColumns.find((c) => c.key === openFilterKey);
      const isDateFilter = openColumn?.filter?.kind === 'dateRange';
      const width = isDateFilter ? 190 : 320;
      const viewportPadding = 8;
      const maxLeft = window.innerWidth - width - viewportPadding;
      const preferredLeft = rect.right + window.scrollX - width;
      const clampedLeft = Math.max(viewportPadding, Math.min(preferredLeft, maxLeft));
      setPopoverPosition({ top: rect.bottom + window.scrollY + 6, left: clampedLeft, width });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [openFilterKey, visibleColumns]);

  const handleSortClick = (key) => {
    setSort((prev) => {
      const current = Array.isArray(prev) && prev[0]?.key === key ? prev[0] : null;
      if (!current) return [{ key, dir: 'asc' }];
      if (current.dir === 'asc') return [{ key, dir: 'desc' }];
      return [];
    });
  };

  const renderFilterControl = (column) => {
    const kind = column.filter?.kind;
    const value = draftFilters[column.key] || {};
    if (kind === 'textContains') {
      return (
        <input
          type="text"
          value={value.text || ''}
          onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { text: e.target.value } }))}
          placeholder={`Search ${column.label.toLowerCase()}`}
          className="tpc-can-filter-input"
        />
      );
    }
    if (kind === 'dateRange') {
      return (
        <div className="tpc-can-filter-date-range">
          <label className="tpc-can-filter-date-field">
            <span>From</span>
            <input
              type="date"
              value={value.from || ''}
              onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { ...value, from: e.target.value } }))}
              className="tpc-can-filter-input"
            />
          </label>
          <label className="tpc-can-filter-date-field">
            <span>To</span>
            <input
              type="date"
              value={value.to || ''}
              onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { ...value, to: e.target.value } }))}
              className="tpc-can-filter-input"
            />
          </label>
        </div>
      );
    }
    if (kind === 'multiSelect') {
      const options = facets[column.key] || [];
      const selectedValues = Array.isArray(value.values) ? value.values : [];
      return (
        <select
          multiple
          value={selectedValues}
          onChange={(e) => {
            const values = Array.from(e.target.selectedOptions).map((o) => o.value);
            setDraftFilters((prev) => ({ ...prev, [column.key]: { values } }));
          }}
          className="tpc-can-filter-select"
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    return null;
  };

  const openFilter = (key, buttonElement) => {
    setDraftFilters((prev) => ({ ...prev, [key]: filters[key] || {} }));
    if (buttonElement) filterButtonRefs.current[key] = buttonElement;
    setOpenFilterKey((prev) => (prev === key ? null : key));
  };

  const applyFilter = (column) => {
    const key = column.key;
    const kind = column.filter?.kind;
    const draft = draftFilters[key] || {};
    const nextFilters = { ...filters };
    if (kind === 'textContains') {
      const text = String(draft.text || '').trim();
      if (text) nextFilters[key] = { text };
      else delete nextFilters[key];
    } else if (kind === 'dateRange') {
      const from = draft.from || '';
      const to = draft.to || '';
      if (from || to) nextFilters[key] = { from, to };
      else delete nextFilters[key];
    } else if (kind === 'multiSelect') {
      const values = Array.isArray(draft.values) ? draft.values.filter(Boolean) : [];
      if (values.length > 0) nextFilters[key] = { values };
      else delete nextFilters[key];
    }
    setFilters(nextFilters);
    setOpenFilterKey(null);
  };

  const clearFilter = (column) => {
    const key = column.key;
    setFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setDraftFilters((prev) => ({ ...prev, [key]: {} }));
    setOpenFilterKey(null);
  };

  const filterSummary = visibleColumns
    .map((col) => {
      const filter = filters[col.key];
      if (!filter) return null;
      if (col.filter?.kind === 'textContains') return `${col.label}: ${filter.text}`;
      if (col.filter?.kind === 'dateRange') return `${col.label}: ${filter.from || 'Any'} to ${filter.to || 'Any'}`;
      if (col.filter?.kind === 'multiSelect') return `${col.label}: ${(filter.values || []).join(', ')}`;
      return null;
    })
    .filter(Boolean);

  const openColumn = visibleColumns.find((c) => c.key === openFilterKey);
  const filterPopover = openColumn
    ? createPortal(
        <div
          className="tpc-can-filter-popover tpc-can-filter-popover-portal"
          style={{ top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px`, width: `${popoverPosition.width}px` }}
        >
          {renderFilterControl(openColumn)}
          <div className="tpc-can-filter-popover-actions">
            <button type="button" className="btn btn-secondary" onClick={() => clearFilter(openColumn)}>Clear</button>
            <button type="button" className="btn btn-primary" onClick={() => applyFilter(openColumn)}>Apply</button>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <section className="tpc-can-section">
      <div className="tpc-can-section-header">
        <h4>{title}</h4>
        <div className="tpc-can-section-header-right">
          <span className="tpc-can-section-count">{displayedRows.length} rows</span>
          <div className="tpc-can-section-actions no-print">
            <button type="button" className="btn btn-secondary" onClick={() => onExportSectionCsv({ sectionKey, title, columns: visibleColumns, rows: displayedRows })}>
              Export CSV
            </button>
            <button type="button" className="btn btn-primary" onClick={() => onPrintSection(sectionKey)}>
              Print
            </button>
          </div>
        </div>
      </div>
      {filterSummary.length > 0 && <p className="tpc-can-filter-summary">Filters: {filterSummary.join(' | ')}</p>}
      <div className="tpc-can-table-wrap">
        <table className="tpc-can-table">
          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <th key={col.key} onClick={() => col.sortable && handleSortClick(col.key)} className={col.sortable ? 'tpc-can-sortable' : ''}>
                  <div className="tpc-can-header-cell">
                    <span className="tpc-can-header-label">
                      {col.label}
                      {sort[0]?.key === col.key ? (sort[0].dir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </span>
                    {col.filter && (
                      <div className="tpc-can-header-filter-wrap" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="tpc-can-filter-trigger" onClick={(e) => openFilter(col.key, e.currentTarget)} aria-label={`Filter ${col.label}`}>
                          <svg className="tpc-can-filter-trigger-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M4 6H20L14 13V18L10 20V13L4 6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedRows.length === 0 ? (
              <tr><td colSpan={visibleColumns.length}>{emptyText}</td></tr>
            ) : (
              displayedRows.map((row, idx) => (
                <tr key={`${idx}-${row.client_name || 'row'}-${row.birthdate || ''}`}>
                  {visibleColumns.map((col) => {
                    const raw = row[col.key];
                    let display;
                    if (col.type === 'date') display = formatDate(raw);
                    else if (col.key === 'cs_latest_visit_display') {
                      if (raw == null || raw === '') display = '—';
                      else if (raw === 'OOR') display = 'OOR';
                      else display = formatDate(raw);
                    } else display = raw || '—';
                    return <td key={`${idx}-${col.key}`}>{display}</td>;
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {filterPopover}
    </section>
  );
}

const SECTION_DEFS = [
  {
    key: 'mismatches',
    title: 'Access Exit is null or not equal to Cornerstone latest visit (within 120 days and within 45 days of age 3)',
    emptyText: 'None',
    columns: [
      { key: 'client_name', label: 'Client', type: 'string', sortable: true, visibleByDefault: true, filter: { kind: 'textContains' } },
      { key: 'birthdate', label: 'Birthdate', type: 'date', sortable: true, visibleByDefault: true, filter: { kind: 'dateRange' } },
      { key: 'access_exit_date', label: 'Access Exit', type: 'date', sortable: true, visibleByDefault: true, filter: { kind: 'dateRange' } },
      {
        key: 'cs_latest_visit_display',
        label: 'CS Latest visit',
        type: 'string',
        sortable: true,
        visibleByDefault: true,
        filter: { kind: 'textContains' },
      },
      { key: 'staff_last', label: 'Staff', type: 'string', sortable: true, visibleByDefault: true, filter: { kind: 'multiSelect' } },
      { key: 'ifsp_date', label: 'IFSP Date', type: 'date', sortable: true, visibleByDefault: true, filter: { kind: 'dateRange' } },
      { key: 'age3', label: 'Age 3', type: 'date', sortable: true, visibleByDefault: true, filter: { kind: 'dateRange' } },
      { key: 'days_to_age3', label: 'Days to Age 3', type: 'number', sortable: true, visibleByDefault: true },
      { key: 'mismatch_reason', label: 'Mismatch Reason', type: 'string', sortable: true, visibleByDefault: true, filter: { kind: 'multiSelect' } },
    ],
  },
];

function ExitAccessVsCornerstoneReport({ refreshKey = 0, tenantCode = null }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadedAt, setUploadedAt] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [printScope, setPrintScope] = useState('all');
  const [runAt] = useState(() => new Date());

  const runReport = useCallback(() => {
    setLoading(true);
    setError(null);
    return reportsAPI.get('exit-access-vs-cornerstone')
      .then((res) => {
        setData(res.data);
        if (res.data?.uploaded_at) setUploadedAt(res.data.uploaded_at);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || err?.message || 'Failed to load report');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    runReport();
  }, [runReport, refreshKey]);

  useEffect(() => {
    const onAfterPrint = () => setPrintScope('all');
    window.addEventListener('afterprint', onAfterPrint);
    return () => window.removeEventListener('afterprint', onAfterPrint);
  }, []);

  const handleUpload = () => {
    if (!file) {
      setUploadError('Choose a chldeild .csv or .txt file first.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    setUploadSuccess('Uploading and processing file... this can take a minute on Azure.');
    reportsAPI.uploadExitCornerstone(file)
      .then((res) => {
        setUploadedAt(res.data?.uploaded_at);
        setFile(null);
        const rowCount = Number(res.data?.row_count || 0);
        setUploadSuccess(`Upload complete (${rowCount.toLocaleString()} rows). Refreshing results...`);
        return runReport().then(() => setUploadSuccess('Upload complete. Results refreshed.'));
      })
      .catch((err) => {
        setUploadError(err?.response?.data?.error || err?.message || 'Upload failed');
        setUploadSuccess(null);
      })
      .finally(() => setUploading(false));
  };

  const handlePrintSection = (key) => {
    setPrintScope(key);
    setTimeout(() => window.print(), 0);
  };

  const handleExportSectionCsv = ({ title, columns, rows }) => {
    const normalizedTitle = String(title || 'section')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    const filename = `exit-reconciliation-${normalizedTitle || 'section'}.csv`;
    const headers = columns.map((c) => c.label);
    const body = (rows || []).map((row) =>
      columns.map((c) => {
        const raw = row[c.key];
        if (c.key === 'cs_latest_visit_display') {
          if (raw == null || raw === '') return '';
          if (raw === 'OOR') return 'OOR';
          return formatDateForCsv(raw);
        }
        if (c.type === 'date') return formatDateForCsv(raw);
        return raw == null ? '' : String(raw);
      })
    );
    downloadCsv(filename, headers, body);
  };

  const sections = useMemo(() => {
    const payload = data || {};
    return SECTION_DEFS.map((def) => ({ ...def, rows: payload[def.key] || [] }));
  }, [data]);

  const titleSuffix = tenantCode ? ` - ${tenantCode}` : '';

  return (
    <div className="tpc-access-vs-cornerstone-report">
      <div className="tpc-report-actions no-print">
        <div className="tpc-upload-row">
          <label className="tpc-upload-label">
            <span className="tpc-upload-label-text">chldeild file (from Foxfire Exitrpt):</span>
            <input
              type="file"
              accept=".csv,.txt"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setUploadError(null); setUploadSuccess(null); }}
              className="tpc-file-input"
            />
          </label>
          <button type="button" onClick={handleUpload} className="btn btn-primary" disabled={uploading || !file}>
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
          {uploadError && <span className="tpc-upload-error">{uploadError}</span>}
          {uploadSuccess && <span className="tpc-upload-success">{uploadSuccess}</span>}
        </div>
        {uploadedAt && <p className="tpc-uploaded-at">Last import: {formatDate(uploadedAt)}{loading ? ' · Refreshing results...' : ''}</p>}
      </div>

      {error && <div className="tpc-report-error">{error}</div>}
      {loading && !data && <div className="tpc-report-loading">Loading reconciliation...</div>}

      {data && !error && (
        <div className="tpc-report-sections">
          <h3 className="tpc-report-main-title">Exit Reconciliation (Access vs Cornerstone){titleSuffix}</h3>
          <div className="tpc-print-meta print-only">
            <div>Run at: {formatDateTime(runAt)}</div>
            <div>Last import: {uploadedAt ? formatDateTime(uploadedAt) : '—'}</div>
            <div>Scope: {printScope === 'all' ? 'All sections' : (sections.find((s) => s.key === printScope)?.title || 'Selected section')}</div>
          </div>

          {sections.map((section) => (
            <div
              key={section.key}
              className={printScope !== 'all' && printScope !== section.key ? 'tpc-print-hidden' : ''}
            >
              <ReconciliationGrid
                sectionKey={section.key}
                title={section.title}
                rows={section.rows}
                columns={section.columns}
                emptyText={section.emptyText}
                onPrintSection={handlePrintSection}
                onExportSectionCsv={handleExportSectionCsv}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ExitAccessVsCornerstoneReport;
